import { formatPhoneNumber } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import React from "react";
import {
    ConversationFeedBasicsFragment,
    VoiceCallDirection,
} from "../../generated/graphql";
import { formatCallDuration } from "./call-duration";
import { useVoiceCallTranscriptQuery } from "./use-voice-call-transcript";

type Thread = ConversationFeedBasicsFragment["threads"][number];
type VoiceCallNode = NonNullable<Thread["voiceCall"]>;

interface VoiceCallDetailInlineProps {
    voiceCall: VoiceCallNode;
    remoteContactName: string | null;
}

const CALLER_MEDIA_CHANNEL = 1;

const isRecordingPlayable = (voiceCall: VoiceCallNode): boolean =>
    voiceCall.recordingSid !== null && voiceCall.recordingStatus === "completed";

export const voiceCallStatusPreview = (voiceCall: VoiceCallNode): string => {
    if (!voiceCall.recordingSid) {
        return "No recording";
    }
    if (voiceCall.recordingStatus !== "completed") {
        return "Recording uploading…";
    }
    if (voiceCall.recordingDurationSec) {
        return `${formatCallDuration(voiceCall.recordingDurationSec)} call`;
    }
    if (voiceCall.transcriptStatus === null || voiceCall.transcriptStatus === "queued") {
        return "Transcript in progress…";
    }
    return "Call recorded";
};

interface StatusBanner {
    type: "info" | "warning";
    message: string;
}

const statusBannerFor = (voiceCall: VoiceCallNode): StatusBanner | null => {
    if (voiceCall.transcriptStatus === "failed") {
        if (isRecordingPlayable(voiceCall)) {
            return {
                type: "warning",
                message:
                    "Transcript could not be generated for this call. The recording is still playable below.",
            };
        }
        return {
            type: "warning",
            message: "Transcript could not be generated for this call.",
        };
    }
    if (!voiceCall.recordingSid) {
        return {
            type: "info",
            message: "No recording is available for this call.",
        };
    }
    if (voiceCall.recordingStatus !== "completed") {
        return { type: "info", message: "Recording is uploading…" };
    }
    if (voiceCall.transcriptStatus === null || voiceCall.transcriptStatus === "queued") {
        return { type: "info", message: "Transcript is being generated…" };
    }
    return null;
};

const speakerLabelFor = (
    voiceCall: VoiceCallNode,
    remoteContactName: string | null,
    mediaChannel: number,
): string => {
    const isInbound = voiceCall.direction === VoiceCallDirection.Inbound;
    const remoteLabel =
        remoteContactName ?? formatPhoneNumber(isInbound ? voiceCall.from : voiceCall.to);
    const internalLabel = voiceCall.createdByUser?.displayName ?? "Unknown";
    const isCallerChannel = mediaChannel === CALLER_MEDIA_CHANNEL;
    if (isInbound) {
        return isCallerChannel ? remoteLabel : internalLabel;
    }
    return isCallerChannel ? internalLabel : remoteLabel;
};

export const VoiceCallDetailInline: React.FC<VoiceCallDetailInlineProps> = props => {
    const { voiceCall, remoteContactName } = props;
    const banner = statusBannerFor(voiceCall);
    const hasRecording = isRecordingPlayable(voiceCall);
    const recordingUrl = `/api/voice-calls/${voiceCall.id}/recording`;
    const transcriptQuery = useVoiceCallTranscriptQuery(voiceCall.id);
    const sentences = transcriptQuery.data?.sentences ?? [];

    return (
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
            {banner ? (
                <InlineBanner type={banner.type}>{banner.message}</InlineBanner>
            ) : null}

            {hasRecording ? (
                <section>
                    <audio
                        controls
                        aria-label="Call recording"
                        className="w-full max-w-lg"
                        src={recordingUrl}
                        preload="metadata"
                    />
                    {voiceCall.recordingDurationSec ? (
                        <p className="text-xs text-gray-500 mt-1 tabular-nums">
                            Duration {formatCallDuration(voiceCall.recordingDurationSec)}
                        </p>
                    ) : null}
                </section>
            ) : null}

            {voiceCall.summary ? (
                <section>
                    <h3 className="text-[10.5px] uppercase tracking-wider font-medium text-gray-500 mb-1.5">
                        AI Summary
                    </h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {voiceCall.summary}
                    </p>
                </section>
            ) : null}

            {voiceCall.transcriptStatus === "completed" &&
            (transcriptQuery.isPending || sentences.length > 0) ? (
                <section>
                    <h3 className="text-[10.5px] uppercase tracking-wider font-medium text-gray-500 mb-1.5">
                        Transcript
                    </h3>
                    {transcriptQuery.isPending ? (
                        <p className="text-sm text-gray-400">Loading transcript…</p>
                    ) : (
                        <ul role="list" className="space-y-2">
                            {sentences.map((sentence, index) => (
                                <li
                                    key={index}
                                    className="flex gap-3 text-sm text-gray-800">
                                    <span className="flex-shrink-0 w-12 text-[11px] tabular-nums text-gray-400 pt-0.5">
                                        {formatCallDuration(sentence.startTime)}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] font-medium text-gray-600 mb-0.5">
                                            {speakerLabelFor(
                                                voiceCall,
                                                remoteContactName,
                                                sentence.mediaChannel,
                                            )}
                                        </div>
                                        <p className="whitespace-pre-wrap">
                                            {sentence.text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            ) : null}
        </div>
    );
};
